/**
 * Individual story item in list/tree - supports drag-drop reordering
 * Story 198: View and filter stories in backlog
 *
 * Uses @atlaskit/pragmatic-drag-and-drop for cross-epic story movement.
 */

import { useEffect, useRef, useState, useCallback, memo } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  attachClosestEdge,
  extractClosestEdge,
  type Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { StatusDot } from './StatusDot';
import { useDeleteStory } from '../../hooks/useStoryMutations';
import type { StoryListItem } from '../../hooks/useStories';
import './StoryNode.css';

interface StoryNodeProps {
  story: StoryListItem;
  /** Project slug - required for delete functionality */
  projectSlug?: string;
  isSelected: boolean;
  onSelect: () => void;
  /** Enable drag-drop (disabled in backlog view) */
  draggable?: boolean;
}

/** Story drag data shape */
export interface StoryDragData {
  type: 'story';
  storyId: string;
  epicSlug: string | null;
  [key: string]: unknown; // Index signature for Record<string, unknown> compatibility
}

export function isStoryDragData(data: Record<string, unknown>): data is StoryDragData {
  return data.type === 'story' && typeof data.storyId === 'string';
}

export const StoryNode = memo(function StoryNode({
  story,
  projectSlug,
  isSelected,
  onSelect,
  draggable: isDraggable = false,
}: StoryNodeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const deleteStory = useDeleteStory();

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!projectSlug) return;

      const message = `Delete story #${story.external_id} "${story.title}"?\n\nThis is a soft delete - the story will be hidden but can be restored later.`;

      if (window.confirm(message)) {
        deleteStory.mutate({ projectSlug, storyExternalId: story.external_id });
      }
    },
    [story.external_id, story.title, projectSlug, deleteStory]
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !isDraggable) return;

    return combine(
      draggable({
        element,
        getInitialData: (): StoryDragData => ({
          type: 'story',
          storyId: story.external_id,
          epicSlug: story.epic_slug,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: ({ input, element }) => {
          return attachClosestEdge(
            { type: 'story', storyId: story.external_id, epicSlug: story.epic_slug },
            { input, element, allowedEdges: ['top', 'bottom'] }
          );
        },
        canDrop: ({ source }) => {
          const data = source.data;
          return isStoryDragData(data) && data.storyId !== story.external_id;
        },
        onDragEnter: ({ self }) => {
          setClosestEdge(extractClosestEdge(self.data));
        },
        onDrag: ({ self }) => {
          setClosestEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [isDraggable, story.external_id, story.epic_slug]);

  return (
    <div
      ref={ref}
      className={`story-node ${isSelected ? 'story-node--selected' : ''} ${isDragging ? 'story-node--dragging' : ''}`}
    >
      {closestEdge === 'top' && <div className="story-node__drop-indicator story-node__drop-indicator--top" />}
      {isDraggable && (
        <div className="story-node__drag-handle" aria-label="Drag to reorder">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M10 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 5a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
          </svg>
        </div>
      )}
      <button className="story-node__content" onClick={onSelect} type="button">
        <StatusDot status={story.status} />
        <span className="story-node__id">{story.external_id}</span>
        <span className="story-node__title">{story.title}</span>
      </button>
      {projectSlug && (
        <button
          className="story-node__delete"
          onClick={handleDelete}
          type="button"
          aria-label="Delete story"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675a.75.75 0 10-1.492.15l.66 6.6A1.75 1.75 0 005.405 15h5.19c.9 0 1.652-.681 1.741-1.576l.66-6.6a.75.75 0 00-1.492-.149l-.66 6.6a.25.25 0 01-.249.225h-5.19a.25.25 0 01-.249-.225l-.66-6.6z"
            />
          </svg>
        </button>
      )}
      {closestEdge === 'bottom' && <div className="story-node__drop-indicator story-node__drop-indicator--bottom" />}
    </div>
  );
});
